import { FC, Fragment, useMemo, useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { CellContext } from "@tanstack/react-table"
import { ChevronRight } from "lucide-react"
import { DateTime } from "luxon"

import Table from "../table"
import Select from "../form/select"
import TimestampCell from "./cells/TimestampCell"
import StandardCell from "./cells/StandardCell"
import PricingProfileCell from "./cells/PricingProfileCell"
import PricingLogCardDetails from "./pricing-log-card-details"
import MonthlyStats, { ChartClickData } from "./monthly-stats"
import FilterChips from "../charts/filter-chips"
import DateFilter, { DateRangePreset } from "../activity/date-filter"
import RefetchButton from "../activity/refetch-button"
import {
  useFetchBrandsQuery,
  useFetchRateflowLogsQuery
} from "../../../redux/queries"
import { useAppSelector } from "../../hooks"
import { selectUserDetails, selectUserRoles } from "../../../redux/slices/auth"
import {
  checkIsSuperAdmin,
  formatPricingLogTypeColumn,
  isNotZeroString
} from "../../utils"
import {
  BBColumnDef,
  PricingLog,
  PricingLogQuery,
  SelectOption,
  SelectOptions
} from "../../../types"

interface Props {
  id?: PricingLogQuery
  isBrandVisible?: boolean
  showBrandSelector?: boolean
}

const getClassName = (props: CellContext<PricingLog, unknown>) => {
  const { base_rate } = props.row.original
  return isNotZeroString(base_rate) ? "" : "bg-red-50"
}

enum PRICING_LOGS_OPTION_IDS {
  ALL_REQUESTS = "all-requests",
  SUCCESSFUL_REQUESTS = "successful-requests",
  FAILED_REQUESTS = "failed-requests"
}

const PRICING_LOGS_OPTIONS: SelectOptions = [
  {
    id: PRICING_LOGS_OPTION_IDS.ALL_REQUESTS,
    name: "All Requests"
  },
  {
    id: PRICING_LOGS_OPTION_IDS.SUCCESSFUL_REQUESTS,
    name: "Successful Requests"
  },
  {
    id: PRICING_LOGS_OPTION_IDS.FAILED_REQUESTS,
    name: "Failed Requests"
  }
]

const ALL_BRANDS_OPTION: SelectOption = { id: "all-brands", name: "All Brands" }

const PricingLogs: FC<Props> = ({ id, isBrandVisible, showBrandSelector }) => {
  const navigate = useNavigate()
  const { quoteId } = useParams<{ quoteId?: string }>()
  const userRoles = useAppSelector(selectUserRoles)
  const isSuperAdmin = checkIsSuperAdmin(userRoles)
  const user = useAppSelector(selectUserDetails)
  const [selected, setSelected] = useState<PricingLog>()
  const [selectedOption, setSelectedOption] = useState<string>(
    PRICING_LOGS_OPTIONS[0].id
  )
  const [selectedBrand, setSelectedBrand] = useState<string>(
    () => id?.bank_id ?? ALL_BRANDS_OPTION.id
  )
  const [chartFilter, setChartFilter] = useState<ChartClickData | null>(null)
  const [selectedDateRange, setSelectedDateRange] =
    useState<DateRangePreset>("today")
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null)
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null)

  const { data: brands = [] } = useFetchBrandsQuery(
    isSuperAdmin ? { limit: 999 } : { org_id: user?.org?.id, limit: 999 },
    {
      skip: !showBrandSelector
    }
  )

  // Build options for the brand select
  const brandOptions: SelectOptions = useMemo(
    () => [
      ALL_BRANDS_OPTION,
      ...brands.map(brand => ({ id: brand.id, name: brand.name }))
    ],
    [brands]
  )

  // Determine the current id for the query
  const currentId: PricingLogQuery | undefined = useMemo(() => {
    return showBrandSelector && selectedBrand !== ALL_BRANDS_OPTION.id
      ? { bank_id: selectedBrand }
      : id
  }, [id, selectedBrand, showBrandSelector])

  // Calculate date range based on chartFilter, dateFilter, or default period
  const { minCreated, maxCreated } = useMemo(() => {
    if (chartFilter) {
      // Use chart filter to determine exact date range
      if (chartFilter.viewType === "daily") {
        // Parse ISO date string (e.g., "2025-10-29")
        const date = new Date(chartFilter.period)
        const startOfDay = new Date(date.setHours(0, 0, 0, 0))
        const endOfDay = new Date(date.setHours(23, 59, 59, 999))

        return {
          minCreated: Math.floor(startOfDay.getTime() / 1000),
          maxCreated: Math.floor(endOfDay.getTime() / 1000)
        }
      } else {
        // Monthly view: "2025-January"
        const [year, monthName] = chartFilter.period.split("-")
        const monthMap: Record<string, number> = {
          January: 0,
          February: 1,
          March: 2,
          April: 3,
          May: 4,
          June: 5,
          July: 6,
          August: 7,
          September: 8,
          October: 9,
          November: 10,
          December: 11
        }
        const month = monthMap[monthName]

        const startOfMonth = new Date(parseInt(year), month, 1, 0, 0, 0, 0)
        const endOfMonth = new Date(
          parseInt(year),
          month + 1,
          0,
          23,
          59,
          59,
          999
        )

        return {
          minCreated: Math.floor(startOfMonth.getTime() / 1000),
          maxCreated: Math.floor(endOfMonth.getTime() / 1000)
        }
      }
    } else if (selectedDateRange !== "all") {
      // Use date filter
      const now = DateTime.now()

      switch (selectedDateRange) {
        case "today":
          return {
            minCreated: now.startOf("day").toSeconds(),
            maxCreated: now.endOf("day").toSeconds()
          }
        case "last_7":
          return {
            minCreated: now.minus({ days: 7 }).startOf("day").toSeconds(),
            maxCreated: now.endOf("day").toSeconds()
          }
        case "last_30":
          return {
            minCreated: now.minus({ days: 30 }).startOf("day").toSeconds(),
            maxCreated: now.endOf("day").toSeconds()
          }
        case "last_90":
          return {
            minCreated: now.minus({ days: 90 }).startOf("day").toSeconds(),
            maxCreated: now.endOf("day").toSeconds()
          }
        case "custom":
          if (customStartDate && customEndDate) {
            return {
              minCreated: DateTime.fromJSDate(customStartDate)
                .startOf("day")
                .toSeconds(),
              maxCreated: DateTime.fromJSDate(customEndDate)
                .endOf("day")
                .toSeconds()
            }
          }
          return { minCreated: undefined, maxCreated: undefined }
        default:
          return { minCreated: undefined, maxCreated: undefined }
      }
    } else {
      // Default behavior: show all
      return {
        minCreated: undefined,
        maxCreated: undefined
      }
    }
  }, [chartFilter, selectedDateRange, customStartDate, customEndDate])

  const {
    data: pricingLogsAll,
    isFetching: isPricingLogsLoading,
    refetch
  } = useFetchRateflowLogsQuery({
    min_created: minCreated,
    max_created: maxCreated,
    success_only: chartFilter?.status === "success" ? 1 : undefined,
    failed_only: chartFilter?.status === "failed" ? 1 : undefined,
    ...currentId
  })

  const pricingLogs = useMemo(
    () =>
      currentId?.bank_id
        ? pricingLogsAll?.filter(pl => pl.brand_id === currentId.bank_id)
        : pricingLogsAll,
    [currentId?.bank_id, pricingLogsAll]
  )

  const sortedList = useMemo(
    () =>
      pricingLogs?.length
        ? [...pricingLogs].sort(
            (a, b) => parseInt(b.timestamp) - parseInt(a.timestamp)
          )
        : [],
    [pricingLogs]
  )

  const columns: BBColumnDef<PricingLog>[] = useMemo(
    () => [
      {
        id: "timestamp",
        header: "Timestamp",
        accessorFn: row => row.timestamp,
        footer: props => props.column.id,
        cell: props => (
          <TimestampCell
            value={props.getValue() as string}
            className={`pl-2 pr-3 py-4 hidden md:table-cell ${getClassName(
              props
            )}`}
          />
        ),
        meta: { headerClassName: "hidden md:table-cell w-[220px]" }
      },
      {
        id: "ob_profile_name",
        header: "Pricing Profile",
        accessorFn: row => row.ob_profile_name,
        footer: props => props.column.id,
        cell: props => (
          <PricingProfileCell
            value={props.row.original}
            className={getClassName(props)}
          />
        )
      },
      {
        id: "pricing-log-id",
        header: "Quote ID",
        meta: { headerClassName: "hidden" },
        accessorFn: row => row.id,
        cell: () => <></>
      }
    ],
    []
  )

  const loanOfficerColumn: BBColumnDef<PricingLog> = useMemo(
    () => ({
      id: "lo_name",
      header: "Loan Officer",
      accessorFn: row => row.lo_name,
      footer: props => props.column.id,
      cell: props => (
        <StandardCell
          value={props.getValue() as string}
          className={`py-4 hidden md:table-cell ${getClassName(props)}`}
        />
      ),
      meta: {
        headerClassName: "hidden md:table-cell w-[180px]"
      }
    }),
    []
  )

  const brandColumn: BBColumnDef<PricingLog> = useMemo(
    () => ({
      id: "brand_name",
      header: "Brand",
      accessorFn: row => row.brand_name,
      footer: props => props.column.id,
      cell: props => (
        <StandardCell
          value={props.getValue() as string}
          className={`py-4 hidden md:table-cell ${getClassName(props)}`}
        />
      ),
      meta: { headerClassName: "hidden md:table-cell w-[180px]" }
    }),
    []
  )

  const typeColumn: BBColumnDef<PricingLog> = useMemo(
    () => ({
      id: "type",
      header: "Type",
      accessorFn: row => row.source,
      footer: props => props.column.id,
      cell: props => (
        <StandardCell
          value={formatPricingLogTypeColumn(props.getValue() as string)}
          className={`py-4 hidden md:table-cell ${getClassName(props)}`}
        />
      ),
      meta: { headerClassName: "hidden md:table-cell w-[180px]" }
    }),
    []
  )

  const editColumn: BBColumnDef<PricingLog> = useMemo(
    () => ({
      id: "edit",
      header: "",
      meta: { headerClassName: "w-10" },
      footer: props => props.column.id,
      cell: props => (
        <td className={`text-center ${getClassName(props)}`}>
          <ChevronRight
            onClick={() => navigate(`/pricing-log/${props.row.original.id}`)}
            className="cursor-pointer ml-auto"
          />
        </td>
      )
    }),
    [navigate]
  )

  const tableColumns = useMemo(() => {
    if (!currentId?.loid) {
      return [...columns, loanOfficerColumn, typeColumn, editColumn]
    }
    if (isBrandVisible) {
      return [...columns, brandColumn, typeColumn, editColumn]
    }

    return [...columns, typeColumn, editColumn]
  }, [
    brandColumn,
    columns,
    typeColumn,
    editColumn,
    currentId?.loid,
    isBrandVisible,
    loanOfficerColumn
  ])

  const closeCardDetailsPopOver = () => {
    setSelected(undefined)
    if (quoteId) {
      navigate("/pricing-log")
    }
  }

  const handleChange = (selectedId: string) => {
    setSelectedOption(selectedId)
  }

  const handleDateRangeChange = (
    range: DateRangePreset,
    startDate?: Date,
    endDate?: Date
  ) => {
    setSelectedDateRange(range)
    if (range === "custom" && startDate && endDate) {
      setCustomStartDate(startDate)
      setCustomEndDate(endDate)
    }
    // Clear chart filter when manually using date filter
    setChartFilter(null)
  }

  // Open slideout when URL has quoteId
  useEffect(() => {
    if (quoteId) {
      setSelected({ id: quoteId } as PricingLog)
    }
  }, [quoteId])

  // Sync date filter when chart is clicked
  useEffect(() => {
    if (chartFilter) {
      if (chartFilter.viewType === "daily") {
        // Parse ISO date string (e.g., "2025-10-29")
        const date = new Date(chartFilter.period)
        setSelectedDateRange("custom")
        setCustomStartDate(date)
        setCustomEndDate(date)
      } else {
        // Monthly view: "2025-January"
        const [year, monthName] = chartFilter.period.split("-")
        const monthMap: Record<string, number> = {
          January: 0,
          February: 1,
          March: 2,
          April: 3,
          May: 4,
          June: 5,
          July: 6,
          August: 7,
          September: 8,
          October: 9,
          November: 10,
          December: 11
        }
        const month = monthMap[monthName]

        const startOfMonth = new Date(parseInt(year), month, 1)
        const endOfMonth = new Date(parseInt(year), month + 1, 0)

        setSelectedDateRange("custom")
        setCustomStartDate(startOfMonth)
        setCustomEndDate(endOfMonth)
      }
    }
  }, [chartFilter])

  const handleClearDateFilter = () => {
    setSelectedDateRange("all")
    setCustomStartDate(null)
    setCustomEndDate(null)
  }

  const filteredDataList = useMemo(() => {
    switch (selectedOption) {
      case PRICING_LOGS_OPTION_IDS.SUCCESSFUL_REQUESTS:
        return sortedList.filter(item => isNotZeroString(item.base_rate))
      case PRICING_LOGS_OPTION_IDS.FAILED_REQUESTS:
        return sortedList.filter(item => !isNotZeroString(item.base_rate))
      default:
        return sortedList
    }
  }, [selectedOption, sortedList])

  return (
    <div className="px-6 py-6">
      <Table
        columns={tableColumns}
        data={filteredDataList}
        viewType="table"
        searchPlaceholder="Search logs"
        canChangeType={false}
        shrinkFilterSort
        noSorter
        extraHeaderElement={
          <Fragment>
            <Select
              label=""
              selected={selectedOption}
              data={PRICING_LOGS_OPTIONS}
              IDkey="id"
              selectClassName="w-40 mb-2 md:mb-0"
              buttonClassName="!px-4 !py-2 !rounded-lg !border-slate-300 !shadow-none"
              setSelected={handleChange}
            />
            {showBrandSelector && (
              <Select
                label=""
                selected={selectedBrand}
                data={brandOptions}
                IDkey="id"
                selectClassName="w-40 ml-0 md:ml-2 mb-2 md:mb-0"
                buttonClassName="!px-4 !py-2 !rounded-lg !border-slate-300 !shadow-none"
                setSelected={setSelectedBrand}
              />
            )}
            <div className="ml-auto flex items-center gap-2">
              <DateFilter
                selectedRange={selectedDateRange}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                onRangeChange={handleDateRangeChange}
                onClearFilter={handleClearDateFilter}
              />
              <RefetchButton onClick={refetch} />
            </div>
          </Fragment>
        }
        extraHeaderElementClassName="w-full flex flex-col md:flex-row"
        searchClassName="pl-10 py-3 lg:min-w-[20rem] text-sm"
        extraElementBeforeTable={
          <Fragment>
            {showBrandSelector && (
              <MonthlyStats
                orgId={currentId?.org_id}
                brandId={currentId?.bank_id}
                onChartClick={setChartFilter}
                selectedBarIndex={chartFilter?.index ?? null}
              />
            )}
            {/* Filter Chips - shown when chart filter is active */}
            {chartFilter && (
              <FilterChips
                filter={chartFilter}
                onClear={() => setChartFilter(null)}
                currentCount={sortedList.length}
                totalCount={pricingLogs?.length || 0}
                itemLabel="logs"
              />
            )}
          </Fragment>
        }
        pageSize={25}
        isLoading={isPricingLogsLoading}
      />
      <PricingLogCardDetails
        open={!!selected}
        onClose={closeCardDetailsPopOver}
        data={selected}
      />
    </div>
  )
}

export default PricingLogs
